<template>
  <div>
    <div class="d-flex flex-row">
      <div class="box-header">
        <TotalCount :totals="items?.length || 0" :data="[]" />
      </div>
      <div
        v-show="selectedIds.length"
        @click="onClearSelect"
        class="text-nowrap ml-auto text-primary cursor-pointer clean-select"
        v-b-tooltip.hover
        :title="`Click để bỏ chọn, chọn tối đa ${maxSelect}`"
      >
        Đã chọn:
        <strong class="text-danger">{{ selectedIds.length.toLocaleString('vi-VN') }}</strong>
      </div>
    </div>

    <b-table
      hover
      head-variant="light"
      responsive
      :items="items"
      :fields="fields"
      :fixed="true"
      :bordered="true"
      :busy.sync="loading"
      :tbody-tr-class="rowClass"
      style="min-height: 500px"
    >
      <template #row-details="row">
        <b-card>
          <span class="text-danger">{{ row.item.errorMessage }}</span>
        </b-card>
      </template>

      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>

      <template #empty>
        <div class="text-center text-info my-2">Không có dữ liệu</div>
      </template>

      <template #emptyfiltered="{ emptyFilteredText }">
        <h4>{{ emptyFilteredText }}</h4>
      </template>

      <template #cell(index)="{ index }">
        {{ (currentPage - 1) * perPage + index + 1 }}
      </template>

      <template #cell(owner)="{ item }">
        <div>
          <b-link :href="`/list-mc/${item.customerId}`" size="sm" target="_blank">
            {{ item.accountName || '' }}
          </b-link>
        </div>
        <div>
          <a :href="`mailto:${item.email}`">{{ item.email }}</a>
        </div>
        <div>
          <b-icon icon="geo-alt"></b-icon>
          <strong>{{ item.businessRegistrationAddress }}</strong>
        </div>
      </template>

      <template #cell(type)="{ item }">
        <div class="form-input" style="width: 250px">
          <Select2
            v-model="item.type"
            :options="typeOptions"
            placeholder="Chọn nhóm công việc"
            @change="onTypeChange(item)"
          />
        </div>
        <div class="form-input mt-2" style="width: 250px">
          <Select2
            v-model="item.purposes"
            :options="item.purposesOptions || []"
            :placeholder="item.loadingPurposes ? 'Loading...' : 'Chọn tên công việc'"
            :disabled="!item.type || !item.purposesOptions?.length"
          />
        </div>
      </template>

      <template #cell(methodCare)="{ item }">
        <div class="form-input" style="width: 250px">
          <Select2
            v-model="item.assigneeId"
            :options="item.staffOptions || []"
            :placeholder="item.loadingStaff ? 'Loading...' : 'Chọn người xử lý'"
          />
        </div>

        <div class="form-input mt-2" style="width: 250px">
          <Select2
            v-model="item.methodCare"
            :options="methodCareOptions"
            placeholder="Chọn hình thức chăm sóc"
            :disabled="loading"
          />
        </div>

        <div class="form-input mt-2">
          <DateRangePicker
            class="w-100"
            :ranges="false"
            v-model="item.dateRange"
            :locale-data="config.localeData"
          >
            <template v-slot:input="picker">
              <label
                v-if="picker.startDate && picker.endDate"
                style="min-width: 11.5em; margin-left: -5px !important"
                class="text-center"
              >
                {{ picker.startDate | formatDate }} -
                {{ picker.endDate | formatDate }}
              </label>
              <label v-else style="min-width: 13.5em" class="font-size-12 time-placeholder">
                Chọn thời gian dự kiến
              </label>
              <i
                class="bx bx-calendar fonsize-20"
                style="position: absolute; right: 1px; top: 50%; transform: translateY(-50%)"
              ></i>
            </template>
          </DateRangePicker>
        </div>
      </template>

      <template #cell(note)="{ item }">
        <div class="form-input">
          <b-textarea v-model="item.note" placeholder="Nhập lý do" />
        </div>
        <div class="form-input mt-2">
          <b-textarea v-model="item.description" placeholder="Nhập mô tả" />
        </div>
      </template>

      <template #cell(actions)="{ item, index }">
        <b-button
          variant="outline-danger"
          size="sm"
          @click="$emit('remove-item', item, index)"
          :disabled="loading"
        >
          <b-icon icon="trash" />
        </b-button>
      </template>
    </b-table>
  </div>
</template>

<script>
  import TotalCount from '@/components/cskh/Counts.vue'
  import moment from 'moment'

  export default {
    name: 'TaskTable',

    components: {
      TotalCount,
    },

    props: {
      // Data props
      items: {
        type: Array,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      typeOptions: {
        type: Array,
        default: () => [],
      },
      methodCareOptions: {
        type: Array,
        default: () => [],
      },
      selectedIds: {
        type: Array,
        default: () => [],
      },
      maxSelect: {
        type: Number,
        default: 200,
      },
      config: {
        type: Object,
        required: true,
      },
      currentPage: {
        type: Number,
        default: 1,
      },
      perPage: {
        type: Number,
        default: 20,
      },
    },

    data() {
      return {
        fields: [
          {
            key: 'index',
            label: 'STT',
            thStyle: 'width: 30px',
            tdClass: 'text-center',
          },
          {
            key: 'owner',
            label: 'Thông tin MA',
            sortable: false,
            thStyle: 'min-width: 150px, max-width: 200px',
            tdClass: 'td_class',
          },
          {
            key: 'type',
            label: 'Nhóm công việc/Công việc',
            sortable: false,
          },
          {
            key: 'methodCare',
            label: 'Người xử lý/Hình thức/Thời gian',
            sortable: false,
          },
          {
            key: 'note',
            label: 'Lý do/Mô tả',
            sortable: false,
            thStyle: 'width: 300px',
          },
          {
            key: 'actions',
            label: 'Thao tác',
            thStyle: 'width: 80px',
            tdClass: 'text-center',
          },
        ],
      }
    },

    methods: {
      rowClass(item, type) {
        if (!item || type !== 'row') return
        if (item.createTaskState === 'FAILED') return 'table-danger'
      },

      onTypeChange(item) {
        this.$emit('type-change', item)
      },

      onClearSelect() {
        this.$emit('clear-select')
      },

      formatDate(date) {
        if (!date) return ''
        return moment(date).format('DD/MM/YYYY')
      },
    },
  }
</script>

<style scoped lang="scss">
  .table-danger {
    background-color: rgba(255, 0, 0, 0.1) !important;

    &:hover {
      background-color: rgba(255, 0, 0, 0.2) !important;
    }
  }
</style>
